import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Stack, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem,
  ListItemIcon, DialogContentText, CircularProgress, Avatar, Chip,
  Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import CloseIcon from '@mui/icons-material/Close';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';
import { useTranslation } from '../i18n/i18n';

// Generate a consistent avatar color from a string
function stringToColor(str) {
  const colors = ['primary.main', 'primary.light', 'secondary.main', 'primary.dark', 'grey.600', 'info.main', 'success.light'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(nameOrEmail) {
  if (!nameOrEmail) return '?';
  const parts = nameOrEmail.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
}

export default function AdminUsersSettings() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [admins, setAdmins] = useState([]);
  const [currentEmail, setCurrentEmail] = useState('');
  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAdmin, setMenuAdmin] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchAdmins = () => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/admins`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => setAdmins(data || []))
      .catch(() => setSnackbar({ open: true, message: 'Failed to load admins', severity: 'error' }));
  };

  const fetchCurrent = () => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/check-auth`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data && data.user && data.user.email) {
          setCurrentEmail(data.user.email);
        } else {
          setCurrentEmail('');
        }
      })
      .catch(() => {
        setCurrentEmail('');
      });
  };

  useEffect(() => {
    fetchAdmins();
    fetchCurrent();
  }, []);

  const handleInvite = () => {
    if (!email) {
      setSnackbar({ open: true, message: 'Email is required', severity: 'error' });
      return;
    }
    setInviteLoading(true);
    fetch(`${API_ENDPOINTS.BASEURL}/api/admins/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, fullname, position: 'admin' })
    })
      .then(async res => {
        const data = await res.json();
        if (res.status === 201) {
          setSnackbar({ open: true, message: data.message || 'Admin invited', severity: 'success' });
          setEmail(''); setFullname('');
          setInviteOpen(false);
          fetchAdmins();
        } else {
          setSnackbar({ open: true, message: data.message || 'Invite failed', severity: 'error' });
        }
      })
        .catch(() => setSnackbar({ open: true, message: 'Invite failed', severity: 'error' }))
        .finally(() => setInviteLoading(false));
  };

  const handleDelete = (adminEmail) => {
    setOpenDeleteDialog(true);
    setMenuAdmin(adminEmail);
  };

  const confirmDelete = async () => {
    if (!menuAdmin) return;
    try {
      const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/admins/${encodeURIComponent(menuAdmin)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: deleteReason })
      });
      const data = await res.json();
      if (res.status === 200) {
        setSnackbar({ open: true, message: data.message || 'Admin deleted', severity: 'success' });
        fetchAdmins();
      } else {
        setSnackbar({ open: true, message: data.message || 'Delete failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setDeleteReason('');
      setMenuAdmin(null);
      setAnchorEl(null);
    }
  };

  const handleOpenMenu = (e, adminEmail) => {
    setAnchorEl(e.currentTarget);
    setMenuAdmin(adminEmail);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuAdmin(null);
  };

  const closeInviteDialog = () => {
    setInviteOpen(false);
    setFullname('');
    setEmail('');
  };

  const closeDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteReason('');
    setMenuAdmin(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 42,
              height: 42,
            }}
          >
            <AdminPanelSettingsIcon sx={{ fontSize: 22 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {t('admin_users_settings')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              {(admins.length === 1 ? t('admin_users_subtitle_one') : t('admin_users_subtitle_many')).replace('{count}', admins.length)}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteOpen(true)}
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1.2,
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' },
          }}
        >
          {t('invite_new_admin')}
        </Button>
      </Box>

      {/* Admin List */}
      {admins.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            px: 3,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.300',
            bgcolor: 'grey.50',
          }}
        >
          <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: 'grey.100', color: 'grey.400' }}>
            <PeopleOutlineIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {t('admin_users_empty_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 320 }}>
            {t('admin_users_empty_hint')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}
          >
            {t('invite_new_admin')}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {admins.map((a, idx) => {
            const isSelf = a.email === currentEmail;
            const displayName = a.fullname || a.email;
            const color = stringToColor(a.email);
            return (
              <Box
                key={a.email}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2.5,
                  py: 1.8,
                  borderRadius: 2,
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'grey.50',
                  },
                  ...(idx < admins.length - 1 && {
                    borderBottom: 1,
                    borderColor: 'divider',
                  }),
                }}
              >
                {/* Avatar */}
                <Avatar
                  sx={{
                    bgcolor: color,
                    width: 44,
                    height: 44,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    letterSpacing: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {getInitials(displayName)}
                </Avatar>

                {/* Name + email */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {displayName}
                    </Typography>
                    {isSelf && (
                      <Chip
                        label={t('admin_users_you_badge')}
                        size="small"
                        color="success"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.4,
                    }}
                  >
                    {a.email}
                  </Typography>
                </Box>

                {/* Role chip */}
                {a.position && (
                  <Chip
                    icon={<BadgeIcon sx={{ fontSize: '14px !important', color: 'primary.main !important' }} />}
                    label={a.position}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      height: 26,
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      display: { xs: 'none', sm: 'flex' },
                    }}
                  />
                )}

                {/* Actions */}
                {!isSelf && (
                  <Tooltip title="Actions">
                    <IconButton
                      size="small"
                      aria-label="admin actions"
                      onClick={(e) => handleOpenMenu(e, a.email)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && menuAdmin === a.email}
                  onClose={handleCloseMenu}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    sx: { borderRadius: 2, minWidth: 160 },
                  }}
                >
                  <MenuItem
                    onClick={() => { setAnchorEl(null); setOpenDeleteDialog(true); }}
                    sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.main' + '08' } }}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    {t('delete')}
                  </MenuItem>
                </Menu>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Invite Dialog */}
      <Dialog
        open={inviteOpen}
        onClose={() => !inviteLoading && closeInviteDialog()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('invite_new_admin')}
          </Typography>
          <IconButton onClick={closeInviteDialog} size="small" disabled={inviteLoading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('name')}
              value={fullname}
              onChange={e => setFullname(e.target.value)}
              variant="outlined"
              size="small"
            />
            <TextField
              fullWidth
              label={t('email')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              variant="outlined"
              type="email"
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={closeInviteDialog}
            disabled={inviteLoading}
            sx={{ textTransform: 'none' }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={inviteLoading}
            startIcon={inviteLoading ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {inviteLoading ? t('sending_invitation') : t('invite_new_admin')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('confirm_remove_admin')}
          </Typography>
          <IconButton onClick={closeDeleteDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('confirm_delete_prompt')}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label={t('reason_optional')}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={closeDeleteDialog}
            sx={{ textTransform: 'none' }}
          >
            {t('cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
